/**
 * Hook to fetch and cache current UK fuel prices from the fuel_price_cache.
 * Uses module-level cache to avoid refetching across component mounts.
 */

import { useState, useEffect } from 'react';
import { apiService } from '../services/apiService';

// Module-level cache (persists across mounts within the same session)
let cachedResult = null;
let fetchPromise = null;

const useFuelPrices = () => {
  const [fuelPrices, setFuelPrices] = useState(cachedResult);
  const [isLoading, setIsLoading] = useState(!cachedResult);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (cachedResult) {
      setFuelPrices(cachedResult);
      setIsLoading(false);
      return;
    }

    const fetchPrices = async () => {
      // Deduplicate concurrent requests
      if (!fetchPromise) {
        fetchPromise = apiService.invokeEdgeFunction('get-fuel-prices', {
          method: 'POST',
          body: {},
        });
      }

      try {
        const data = await fetchPromise;

        // Convert pence to pounds for consumer convenience
        const prices = {
          petrol: (data.petrol_ppl || 145) / 100,
          diesel: (data.diesel_ppl || 150) / 100,
          electricity: (data.electricity_pkwh || 25) / 100,
        };

        cachedResult = prices;
        setFuelPrices(prices);
        setError(null);
      } catch (err) {
        setError(err.message || 'Failed to fetch fuel prices');
        // Use fallback prices on error
        const fallback = { petrol: 1.45, diesel: 1.50, electricity: 0.25 };
        setFuelPrices(fallback);
      } finally {
        setIsLoading(false);
        fetchPromise = null;
      }
    };

    fetchPrices();
  }, []);

  return { fuelPrices, isLoading, error };
};

export default useFuelPrices;
